// frontend/src/features/inventory/components/ScannableMaterialPicker.tsx
import { useEffect, useRef, useState, useMemo } from "react";
import { ScannableInput } from "./ScannableInput";

interface MaterialItem {
  id: string;
  code: string;
  name: string;
}

interface Props {
  materials: MaterialItem[];
  value: string; // The selected material ID
  onChange: (value: string) => void;
  onScannedNonMaterial?: (code: string) => void; // Called when a code is scanned but not found in materials
  disabled?: boolean;
}

export function ScannableMaterialPicker({ materials, value, onChange, onScannedNonMaterial, disabled }: Props) {
  const [query, setQuery] = useState("");
  const [isOpen, setIsOpen] = useState(false);
  const [activeIndex, setActiveIndex] = useState(-1);
  const containerRef = useRef<HTMLDivElement>(null);
  const internalClear = useRef(false);
  const skipNextClear = useRef(false);

  // Sync internal query state with the selected value when it changes externally
  // Reset activeIndex when query changes or menu opens/closes
  useEffect(() => {
    setActiveIndex(-1);
  }, [query, isOpen]);
  useEffect(() => {
    if (value) {
      const selected = materials.find((m) => m.id === value);
      if (selected) {
        setQuery(`${selected.code} — ${selected.name}`);
      }
    } else if (internalClear.current) {
      internalClear.current = false;
    } else {
      setQuery("");
    }
  }, [value, materials]);

  useEffect(() => {
    function handleClickOutside(e: MouseEvent) {
      if (containerRef.current && !containerRef.current.contains(e.target as Node)) {
        setIsOpen(false);
      }
    }
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const filteredMaterials = useMemo(() => {
    if (!query) return materials.slice(0, 50);
    const lowerQuery = query.toLowerCase();
    return materials.filter(
      (m) => m.code.toLowerCase().includes(lowerQuery) || m.name.toLowerCase().includes(lowerQuery)
    ).slice(0, 50);
  }, [materials, query]);

  const handleInputChange = (text: string) => {
    setQuery(text);
    if (value) internalClear.current = true;
    onChange(""); // Clear selection when typing
    if (!isOpen) setIsOpen(true);
  };

  const handleSelect = (item: MaterialItem) => {
    onChange(item.id);
    setQuery(`${item.code} — ${item.name}`);
    setIsOpen(false);
  };

    const handleScan = (code: string) => {
      const trimmedCode = code.trim();
      // Exact match for precedence
      const exactMatch = materials.find((m) => m.code.toLowerCase() === trimmedCode.toLowerCase());
      if (exactMatch) {
        handleSelect(exactMatch);
      } else {
        setQuery(trimmedCode); // Put the raw code in the box
        if (value) internalClear.current = true;
        if (isOpen) skipNextClear.current = true; // Prevent the next blur from clearing it only if it will actually trigger
        onChange(""); // Clear any selected material
        setIsOpen(false);
        onScannedNonMaterial?.(trimmedCode);
      }
    };

  const handleBlur = () => {
    // wait slightly to allow click on dropdown item to register before blurring
    setTimeout(() => {
      // we check internal state because this is async. If value is still empty, revert
      setIsOpen(false);
    }, 150);
  };

  // If user leaves the field without a valid selection, clear the box
  useEffect(() => {
    if (!isOpen && !value) {
      if (skipNextClear.current) {
        skipNextClear.current = false;
      } else {
        setQuery("");
      }
    }
  }, [isOpen, value]);

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === "Escape") {
      setIsOpen(false);
    } else if (e.key === "ArrowDown") {
      e.preventDefault();
      if (!isOpen) {
        setIsOpen(true);
      } else {
        setActiveIndex((prev) => Math.min(prev + 1, filteredMaterials.length - 1));
      }
    } else if (e.key === "ArrowUp") {
      e.preventDefault();
      if (isOpen) {
        setActiveIndex((prev) => Math.max(prev - 1, 0));
      }
    } else if (e.key === "Enter") {
      if (isOpen && activeIndex >= 0 && activeIndex < filteredMaterials.length) {
        e.preventDefault();
        e.stopPropagation();
        const selected = filteredMaterials[activeIndex];
        if (selected) {
          handleSelect(selected);
        }
      }
    }
  };

  return (
    <div ref={containerRef} style={{ position: "relative", width: "100%" }}>
      <ScannableInput
        value={query}
        onChange={handleInputChange}
        onScanned={handleScan}
        onFocus={() => setIsOpen(true)}
        onBlur={handleBlur}
        onKeyDown={handleKeyDown}
        placeholder="جست‌وجوی متریال یا اسکن..."
        disabled={disabled}
      />
      
      {/* Dropdown menu */}
      {isOpen && filteredMaterials.length > 0 && (
        <div
          className="brand-card"
          style={{
            position: "absolute",
            top: "100%",
            right: 0,
            left: 0,
            zIndex: 20,
            marginTop: "0.25rem",
            maxHeight: "220px",
            overflowY: "auto",
            padding: "0.25rem",
            background: "var(--color-surface, #fff)", // Fallback to white if var not found
            border: "1px solid var(--glass-border)",
            borderRadius: "8px",
            boxShadow: "0 8px 24px rgba(0,0,0,0.15)",
          }}
        >
          {filteredMaterials.map((item, index) => (
            <div
              key={item.id}
              role="option"
              aria-selected={activeIndex === index}
              onClick={() => handleSelect(item)}
              style={{
                padding: "0.5rem 0.75rem",
                cursor: "pointer",
                borderRadius: "6px",
                color: "var(--color-text)",
                fontSize: "0.9rem",
                background: activeIndex === index ? "var(--hover-bg, rgba(0,0,0,0.05))" : "transparent",
              }}
              onMouseEnter={(e) => {
                setActiveIndex(index);
                e.currentTarget.style.background = "var(--hover-bg, rgba(0,0,0,0.05))";
              }}
              onMouseLeave={(e) => {
                if (activeIndex !== index) {
                  e.currentTarget.style.background = "transparent";
                }
              }}
            >
              {item.code} — {item.name}
            </div>
          ))}
        </div>
      )}
      {isOpen && filteredMaterials.length === 0 && (
        <div
          className="brand-card"
          style={{
            position: "absolute",
            top: "100%",
            right: 0,
            left: 0,
            zIndex: 20,
            marginTop: "0.25rem",
            padding: "0.75rem",
            background: "var(--color-surface, #fff)",
            border: "1px solid var(--glass-border)",
            borderRadius: "8px",
            boxShadow: "0 8px 24px rgba(0,0,0,0.15)",
          }}
        >
          <span style={{ fontSize: "0.85rem", color: "var(--color-text-muted)" }}>
            متریالی با این مشخصات یافت نشد.
          </span>
        </div>
      )}
    </div>
  );
}
